// Incident store — local-first IDB + opt-in Supabase sync.
//
// Mirrors pilotStore.ts exactly for shape and sync semantics.
// On add(), when `irwin_id` is populated the field is treated as
// read-only in the UI (populated from WFIGS search).

import { create } from 'zustand';
import { createKvList } from '../lib/kvList';
import {
  pushRow,
  pushMany,
  deleteRowByClientId,
  pullAll,
  type SyncBindings,
} from '../lib/syncCore';
import { useAuthStore } from './authStore';
import { newClientId } from '../lib/ids';

// ───────────────────── Types ─────────────────────

export type IncidentStatus = 'active' | 'staged' | 'closed' | 'cancelled';

/**
 * One row of the dynamic Aviation Contacts list on an incident. The
 * canonical roster of role keys lives in `lib/aviationContactRoles.ts`;
 * `role` is a free-form string here so 'other' contacts (or future
 * unforeseen roles) can be stored without a schema change. When role
 * is 'other', `customLabel` carries the human title.
 */
export interface AviationContact {
  id: string;
  role: string;
  customLabel?: string;
  name?: string;
  phone?: string;
  email?: string;
  notes?: string;
}

export interface Incident {
  id: string;
  name: string;
  incident_number?: string;
  irwin_id?: string;
  agreement_number?: string;
  resource_order_number?: string;
  financial_code?: string;
  status: IncidentStatus;
  start_date?: string;      // ISO date
  end_date?: string;
  closed_at?: string;
  closed_by?: string;
  latitude?: number;
  longitude?: number;
  location?: string;
  base_of_ops_airport_ident?: string;
  base_of_ops_helibase_name?: string;
  base_of_ops_lat?: number;
  base_of_ops_lon?: number;
  iap_url?: string;
  contract_url?: string;

  /** Dynamic aviation contacts list (replaces the legacy 5 fixed-role
   *  columns). Migration 20260512_0023 added the underlying jsonb
   *  column; old columns remain for backwards compat. */
  aviation_contacts: AviationContact[];

  // Aviation contacts — LEGACY fixed-role columns. Kept as optional so
  // pre-migration local data still hydrates cleanly. New writes go to
  // `aviation_contacts` only.
  aobm_name?: string; aobm_phone?: string; aobm_email?: string;
  helitack_mgr_name?: string; helitack_mgr_phone?: string; helitack_mgr_email?: string;
  helibase_mgr_name?: string; helibase_mgr_phone?: string; helibase_mgr_email?: string;
  div_super_name?: string; div_super_phone?: string; div_super_email?: string;
  ic_name?: string; ic_phone?: string; ic_email?: string;

  /** Per-incident IHOG proximity overrides. NULL/undefined = use the
   *  global default (12 NM contact, 7 NM exclusion). Migration 0029. */
  ihog_contact_nm?: number | null;
  ihog_exclusion_nm?: number | null;

  notes?: string;
  createdAt: number;
  updatedAt: number;
}

// ───────────────────── DB row shape ─────────────────────

interface IncidentRowDb {
  id: string;
  owner_id: string;
  client_id: string | null;
  name: string;
  incident_number: string | null;
  irwin_id: string | null;
  agreement_number: string | null;
  resource_order_number: string | null;
  financial_code: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  closed_at: string | null;
  closed_by: string | null;
  latitude: number | null;
  longitude: number | null;
  location: string | null;
  base_of_ops_airport_ident: string | null;
  base_of_ops_helibase_name: string | null;
  base_of_ops_lat: number | null;
  base_of_ops_lon: number | null;
  iap_url: string | null;
  contract_url: string | null;
  aobm_name: string | null; aobm_phone: string | null; aobm_email: string | null;
  helitack_mgr_name: string | null; helitack_mgr_phone: string | null; helitack_mgr_email: string | null;
  helibase_mgr_name: string | null; helibase_mgr_phone: string | null; helibase_mgr_email: string | null;
  div_super_name: string | null; div_super_phone: string | null; div_super_email: string | null;
  ic_name: string | null; ic_phone: string | null; ic_email: string | null;
  aviation_contacts: AviationContact[] | null;
  ihog_contact_nm: number | null;
  ihog_exclusion_nm: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ───────────────────── Mappers ─────────────────────

function toDbRow(inc: Incident, ownerId: string): Omit<IncidentRowDb, 'id' | 'created_at' | 'updated_at'> {
  return {
    owner_id: ownerId,
    client_id: inc.id,
    name: inc.name ?? '',
    incident_number: inc.incident_number ?? null,
    irwin_id: inc.irwin_id ?? null,
    agreement_number: inc.agreement_number ?? null,
    resource_order_number: inc.resource_order_number ?? null,
    financial_code: inc.financial_code ?? null,
    status: inc.status ?? 'active',
    start_date: inc.start_date ?? null,
    end_date: inc.end_date ?? null,
    closed_at: inc.closed_at ?? null,
    closed_by: inc.closed_by ?? null,
    latitude: inc.latitude ?? null,
    longitude: inc.longitude ?? null,
    location: inc.location ?? null,
    base_of_ops_airport_ident: inc.base_of_ops_airport_ident ?? null,
    base_of_ops_helibase_name: inc.base_of_ops_helibase_name ?? null,
    base_of_ops_lat: inc.base_of_ops_lat ?? null,
    base_of_ops_lon: inc.base_of_ops_lon ?? null,
    iap_url: inc.iap_url ?? null,
    contract_url: inc.contract_url ?? null,
    aobm_name: inc.aobm_name ?? null, aobm_phone: inc.aobm_phone ?? null, aobm_email: inc.aobm_email ?? null,
    helitack_mgr_name: inc.helitack_mgr_name ?? null, helitack_mgr_phone: inc.helitack_mgr_phone ?? null, helitack_mgr_email: inc.helitack_mgr_email ?? null,
    helibase_mgr_name: inc.helibase_mgr_name ?? null, helibase_mgr_phone: inc.helibase_mgr_phone ?? null, helibase_mgr_email: inc.helibase_mgr_email ?? null,
    div_super_name: inc.div_super_name ?? null, div_super_phone: inc.div_super_phone ?? null, div_super_email: inc.div_super_email ?? null,
    ic_name: inc.ic_name ?? null, ic_phone: inc.ic_phone ?? null, ic_email: inc.ic_email ?? null,
    aviation_contacts: inc.aviation_contacts ?? [],
    ihog_contact_nm:   inc.ihog_contact_nm   ?? null,
    ihog_exclusion_nm: inc.ihog_exclusion_nm ?? null,
    notes: inc.notes ?? null,
  };
}

function fromDbRow(row: IncidentRowDb): Incident {
  const localId = row.client_id ?? row.id;
  return {
    id: localId,
    name: row.name ?? '',
    incident_number: row.incident_number ?? undefined,
    irwin_id: row.irwin_id ?? undefined,
    agreement_number: row.agreement_number ?? undefined,
    resource_order_number: row.resource_order_number ?? undefined,
    financial_code: row.financial_code ?? undefined,
    status: (row.status as IncidentStatus) ?? 'active',
    start_date: row.start_date ?? undefined,
    end_date: row.end_date ?? undefined,
    closed_at: row.closed_at ?? undefined,
    closed_by: row.closed_by ?? undefined,
    latitude: row.latitude ?? undefined,
    longitude: row.longitude ?? undefined,
    location: row.location ?? undefined,
    base_of_ops_airport_ident: row.base_of_ops_airport_ident ?? undefined,
    base_of_ops_helibase_name: row.base_of_ops_helibase_name ?? undefined,
    base_of_ops_lat: row.base_of_ops_lat ?? undefined,
    base_of_ops_lon: row.base_of_ops_lon ?? undefined,
    iap_url: row.iap_url ?? undefined,
    contract_url: row.contract_url ?? undefined,
    aobm_name: row.aobm_name ?? undefined, aobm_phone: row.aobm_phone ?? undefined, aobm_email: row.aobm_email ?? undefined,
    helitack_mgr_name: row.helitack_mgr_name ?? undefined, helitack_mgr_phone: row.helitack_mgr_phone ?? undefined, helitack_mgr_email: row.helitack_mgr_email ?? undefined,
    helibase_mgr_name: row.helibase_mgr_name ?? undefined, helibase_mgr_phone: row.helibase_mgr_phone ?? undefined, helibase_mgr_email: row.helibase_mgr_email ?? undefined,
    div_super_name: row.div_super_name ?? undefined, div_super_phone: row.div_super_phone ?? undefined, div_super_email: row.div_super_email ?? undefined,
    ic_name: row.ic_name ?? undefined, ic_phone: row.ic_phone ?? undefined, ic_email: row.ic_email ?? undefined,
    aviation_contacts: Array.isArray(row.aviation_contacts) ? row.aviation_contacts : [],
    ihog_contact_nm:   row.ihog_contact_nm   ?? undefined,
    ihog_exclusion_nm: row.ihog_exclusion_nm ?? undefined,
    notes: row.notes ?? undefined,
    createdAt: row.created_at ? Date.parse(row.created_at) : Date.now(),
    updatedAt: row.updated_at ? Date.parse(row.updated_at) : Date.now(),
  };
}

// ───────────────────── Sync bindings ─────────────────────

const BINDINGS: SyncBindings<Incident, ReturnType<typeof toDbRow>> = {
  tableName: 'incidents',
  upsertConflict: 'owner_id,client_id',
  localIdField: 'id',
  toDbRow,
  fromDbRow: fromDbRow as (row: ReturnType<typeof toDbRow>) => Incident,
};

// ───────────────────── IDB helpers ─────────────────────

const { loadAll, saveAll } = createKvList<Incident>('incidents.v1');

function newId(): string { return newClientId('inc'); }

// ───────────────────── Store ─────────────────────

interface IncidentState {
  list: Incident[];
  hydrated: boolean;
  lastSyncAt: number | null;
  syncing: boolean;
  syncError: string | null;

  hydrate: () => Promise<void>;
  add: (input?: Partial<Incident>) => Promise<Incident>;
  update: (id: string, patch: Partial<Incident>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  syncFromServer: () => Promise<void>;
  syncToServer: () => Promise<void>;
  byId: (id: string | null | undefined) => Incident | null;
}

export const useIncidentStore = create<IncidentState>((set, get) => ({
  list: [],
  hydrated: false,
  lastSyncAt: null,
  syncing: false,
  syncError: null,

  hydrate: async () => {
    if (get().hydrated) return;
    const list = await loadAll();
    set({ list, hydrated: true });
    // Always attempt sync when we have a session at hydrate time. The
    // auth subscription below handles the case where the session
    // arrives AFTER hydrate.
    if (useAuthStore.getState().user) {
      void get().syncFromServer();
    }
  },

  syncFromServer: async () => {
    set({ syncing: true });
    try {
      // PULL-ONLY sync (2026-05-18 boss report: "close incident doesn't
      // stick"). The previous push-first pattern would overwrite the
      // server with whatever the local IDB held at hydrate time, even
      // when local was actually STALE (e.g. another tab/device had
      // already pushed a newer status). On every auth event and on
      // every hydrate we'd resurrect old state.
      //
      // Correct semantics: at hydrate / re-sync time, trust the server.
      // Writes are already pushed eagerly by add() / update() / remove().
      // The dedicated `syncToServer()` action below still exists for the
      // explicit "push all my local edits up" button.
      //
      // Risk: an edit that happened while offline and never reached the
      // server is lost on the next pull. That risk already existed under
      // the old code (the pull step would clobber it anyway after the
      // push completed) and only a proper outbox pattern fixes it
      // — tracked as a future improvement.
      const result = await pullAll(BINDINGS as unknown as SyncBindings<Incident, Incident>);
      if (!result.ok) {
        set({ syncing: false, syncError: result.error ?? 'sync failed' });
        return;
      }
      await saveAll(result.rows);
      set({ list: result.rows, syncing: false, syncError: null, lastSyncAt: Date.now() });
    } catch (e: any) {
      set({ syncing: false, syncError: e?.message ?? String(e) });
    }
  },

  syncToServer: async () => {
    set({ syncing: true });
    const result = await pushMany(get().list, BINDINGS as unknown as SyncBindings<Incident, Incident>);
    set({
      syncing: false,
      syncError: result.ok ? null : (result.error ?? 'sync failed'),
      lastSyncAt: result.ok ? Date.now() : get().lastSyncAt,
    });
  },

  add: async (input) => {
    const now = Date.now();
    const next: Incident = {
      name: '',
      status: 'active',
      aviation_contacts: [],
      ...input,
      id: newId(),
      createdAt: now,
      updatedAt: now,
    };
    const list = [...get().list, next];
    await saveAll(list);
    set({ list });
    void pushRow(next, BINDINGS as unknown as SyncBindings<Incident, Incident>);
    return next;
  },

  update: async (id, patch) => {
    const now = Date.now();
    const list = get().list.map((inc) => {
      if (inc.id !== id) return inc;
      // When transitioning into 'closed' status, auto-populate closed_at
      // if the caller didn't supply one. This way the server (and any
      // other devices that pull) see when the close happened.
      const closeFields: Partial<Incident> = {};
      if (patch.status === 'closed' && !inc.closed_at && !patch.closed_at) {
        closeFields.closed_at = new Date(now).toISOString();
      }
      return { ...inc, ...patch, ...closeFields, id: inc.id, updatedAt: now };
    });
    await saveAll(list);
    set({ list });
    const updated = list.find((inc) => inc.id === id);
    if (updated) {
      // Surface push failures to syncError so the UI can show them.
      // Previously this was fire-and-forget; a failed close looked
      // identical to a successful one until the next pull overwrote
      // local back to the server's stale state.
      const result = await pushRow(updated, BINDINGS as unknown as SyncBindings<Incident, Incident>);
      if (!result.ok) {
        set({ syncError: result.error ?? 'push failed' });
      } else if (get().syncError) {
        set({ syncError: null });
      }
    }
  },

  remove: async (id) => {
    const list = get().list.filter((inc) => inc.id !== id);
    await saveAll(list);
    set({ list });
    void deleteRowByClientId(id, BINDINGS as unknown as SyncBindings<Incident, Incident>);
  },

  byId: (id) => {
    if (!id) return null;
    return get().list.find((inc) => inc.id === id) ?? null;
  },
}));

// ⚠️ Important: subscribe fires whenever auth state changes. We sync
// on ANY transition to a signed-in user (covers the cold-start race
// where hydrate() ran before auth settled — boss lost a "Lunsbury"
// incident this way on 2026-05-12). The previous version required
// `hydrated === true` already, which meant the sync never fired when
// hydrate happened AFTER auth resolved AND there was no signed-out
// → signed-in transition observed at the same time.
useAuthStore.subscribe((state, prev) => {
  if (!state.user) return;
  if (prev.user === state.user) return;
  // Lazily hydrate first so the local list is populated, then sync.
  const store = useIncidentStore.getState();
  if (!store.hydrated) {
    void store.hydrate(); // hydrate will trigger syncFromServer itself
  } else {
    void store.syncFromServer();
  }
});
